import { CANADIAN_PROVINCES, PROJECT_TYPES } from "../types/index.js";
import type { EstimateWarning, EstimatorInput, ProjectType } from "../types/index.js";

/**
 * Audit fix (Phase 23, Full Application Red-Team Audit Finding 7): project
 * types that cover more than a single interior room - a whole floor, a
 * whole property, or an outdoor structure - where "unusually large for a
 * single-room project" is simply inaccurate (a 6,000 sqft whole-home
 * renovation is a large but entirely real input, not a likely typo) and
 * where a much higher square footage is plausible in the first place.
 */
const MULTI_AREA_PROJECT_TYPES = new Set<ProjectType>([
  "whole_home_renovation",
  "addition",
  "basement",
  "basement_suite",
  "exterior",
  "deck_patio"
]);
const SINGLE_ROOM_IMPLAUSIBLE_THRESHOLD_SQFT = 10_000;
const MULTI_AREA_IMPLAUSIBLE_THRESHOLD_SQFT = 25_000;

/**
 * Runtime validation of an EstimatorInput, independent of TypeScript's
 * compile-time checks. TypeScript guarantees shape at compile time for
 * code written against this contract, but an EstimatorInput can also
 * arrive over a network boundary (a WordPress REST call, a hand-built
 * test fixture, a future non-TS client) where nothing enforces that at
 * runtime. This never throws - it degrades honestly (see
 * docs/calculation-engine.md's "missing/invalid inputs" section) and
 * returns warnings the caller can inspect.
 */
export function validateEstimatorInputAtRuntime(input: EstimatorInput): EstimateWarning[] {
  const warnings: EstimateWarning[] = [];

  if (!input.roomSize || typeof input.roomSize.value !== "number" || input.roomSize.value <= 0) {
    warnings.push({
      code: "room_size_missing_or_invalid",
      message:
        "No valid room size was provided. Size-dependent line items (e.g. base renovation, flooring) could not be calculated and were excluded from this estimate.",
      severity: "blocking"
    });
  } else {
    const isMultiArea = MULTI_AREA_PROJECT_TYPES.has(input.projectType);
    const threshold = isMultiArea ? MULTI_AREA_IMPLAUSIBLE_THRESHOLD_SQFT : SINGLE_ROOM_IMPLAUSIBLE_THRESHOLD_SQFT;
    if (input.roomSize.value > threshold) {
      warnings.push({
        code: "room_size_implausible",
        message: isMultiArea
          ? `Size of ${input.roomSize.value} ${input.roomSize.unit} is unusually large even for this project type and may indicate a data entry error.`
          : `Room size of ${input.roomSize.value} ${input.roomSize.unit} is unusually large for a single-room project and may indicate a data entry error.`,
        severity: "caution"
      });
    }
  }

  if (!PROJECT_TYPES.includes(input.projectType)) {
    warnings.push({
      code: "invalid_project_type",
      message: `"${input.projectType}" is not a recognized project type. No pricing profile can be matched.`,
      severity: "blocking"
    });
  }

  if (!CANADIAN_PROVINCES.includes(input.location.province)) {
    warnings.push({
      code: "invalid_province",
      message: `"${input.location.province}" is not a recognized Canadian province/territory code. Regional pricing fell back to the national baseline.`,
      severity: "caution"
    });
  }

  return warnings;
}
